import { DateTime } from 'luxon'
import { downloadFile, formatDateForDatabase } from '@utils'
import {
    Autocomplete,
    Box,
    Button,
    Divider,
    Paper,
    Popover,
    Stack,
    TextField,
} from '@mui/material'
import { useState } from 'react'
import DashboardLayout from '@pages/Dashboard/components/DashboardLayout'
import DatePicker from '@components/DatePicker'
import DownloadButton from '@components/DownloadButton'
import DownloadErrorDialog from '@components/DownloadErrorDialog'
import PeriodDatePicker from '@components/PeriodDatePicker'
import { STAFF_REPORTS_URL } from '@enums/api'

const DEFAULT_FROM_DATE = DateTime.now().startOf('month')
const DEFAULT_TO_DATE = DateTime.now()
const REPORT_API_ENDPOINT = `${STAFF_REPORTS_URL}/subcontractor-hours`

const SubcontractorHoursReportPage = () => {
    const [fromDate, setFromDate] = useState<DateTime | null | undefined>(
        DEFAULT_FROM_DATE
    )
    const [toDate, setToDate] = useState<DateTime | null | undefined>(
        DEFAULT_TO_DATE
    )
    const [subconId, setSubcontractor] = useState<string | null | undefined>(
        '7441537a-aa25-4ed5-8605-8c12d57d58cc'
    )
    const [downloading, setDownloading] = useState(false)
    const [downloadError, setDownloadError] = useState<string | null>(null)

    const [anchorElement, setAnchorElement] =
        useState<HTMLButtonElement | null>(null)

    const popoverOpen = Boolean(anchorElement)

    const options = [
        {
            label: 'Subcon Alpha (Synner G)',
            id: '7441537a-aa25-4ed5-8605-8c12d57d58cc',
        },
        {
            label: 'Subcon Beta (Azstranorth)',
            id: '6fbcc2b3-3f86-4f63-981f-c22b77a1732e',
        },
    ]

    async function handleDownload() {
        setDownloading(true)
        const from = formatDateForDatabase(fromDate, 'start')
        const to = formatDateForDatabase(toDate, 'end')
        const fileName = `subcontractor-hours_${subconId}${from}-${to}.xlsx`

        // the backend is expecting the "to" to be exclusive, so add a day to
        // fake an inclusive bound
        const query = `?subconId=${subconId}&from=${from}&to=${to}`

        try {
            await downloadFile({
                acceptType:
                    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                fileURL: REPORT_API_ENDPOINT + query,
                saveAsFileName: fileName,
            })
            setDownloading(false)
        } catch (error) {
            console.error(error)
            setDownloading(false)
            setDownloadError('Failed to download report')
        }
    }

    return (
        <DashboardLayout>
            <DownloadErrorDialog
                onClose={() => setDownloadError(null)}
                open={!!downloadError}
                title={downloadError}
            />
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    paddingTop: 64,
                }}
            >
                <Paper
                    style={{
                        paddingInline: 32,
                        paddingBlock: 32,
                        textAlign: 'center',
                    }}
                >
                    <p>
                        Select the date range for this Subcontractor Hours
                        report
                    </p>
                    <div
                        style={{
                            display: 'flex',
                            marginTop: 32,
                            justifyContent: 'center',
                        }}
                    >
                        <div style={{ marginRight: 16 }}>
                            <Autocomplete
                                sx={{ width: 300 }}
                                disablePortal
                                id="standard-basic"
                                disabled={downloading}
                                renderInput={(params) => (
                                    <TextField {...params} label="Subcon" />
                                )}
                                options={options}
                                defaultValue={options[0]}
                                onChange={(e, value) => {
                                    console.log(e.type)
                                    setSubcontractor(value?.id)
                                }}
                            />
                        </div>
                        <Stack direction={'column'}>
                            <Stack direction={'row'}>
                                <div style={{ marginRight: 16 }}>
                                    <DatePicker
                                        disabled={downloading}
                                        label="From"
                                        onChange={setFromDate}
                                        value={fromDate}
                                        maxDate={toDate}
                                    />
                                </div>
                                <div>
                                    <DatePicker
                                        disabled={downloading}
                                        label="To"
                                        onChange={setToDate}
                                        value={toDate}
                                        minDate={fromDate}
                                    />
                                </div>
                            </Stack>
                            <Divider
                                style={{ marginTop: 8 }}
                                variant="fullWidth"
                            />
                            <Box style={{ marginTop: 4 }}>
                                <Button
                                    onClick={(e) =>
                                        setAnchorElement(e.currentTarget)
                                    }
                                >
                                    Quick Set
                                </Button>
                                <Popover
                                    open={popoverOpen}
                                    anchorEl={anchorElement}
                                    onClose={() => setAnchorElement(null)}
                                    anchorOrigin={{
                                        vertical: 'bottom',
                                        horizontal: 'center',
                                    }}
                                    transformOrigin={{
                                        vertical: 'top',
                                        horizontal: 'center',
                                    }}
                                >
                                    <Paper sx={{ padding: 1 }}>
                                        <PeriodDatePicker
                                            fromDate={fromDate}
                                            setFromDate={setFromDate}
                                            toDate={toDate}
                                            setToDate={setToDate}
                                        />
                                    </Paper>
                                </Popover>
                            </Box>
                        </Stack>
                    </div>
                    <div style={{ marginTop: 16 }}>
                        <DownloadButton
                            disabled={
                                downloading || !fromDate || !toDate || !subconId
                            }
                            downloading={downloading}
                            onClick={handleDownload}
                        >
                            Download report
                        </DownloadButton>
                        <p style={{ marginTop: 8 }}>
                            <small>
                                This will download a single-sheet{' '}
                                <code>.xlsx</code> file
                            </small>
                        </p>
                    </div>
                </Paper>
            </div>
        </DashboardLayout>
    )
}

export default SubcontractorHoursReportPage
